/*
 * TOKEN MODEL — intentional decision (do not expand yet).
 * The design docs define visual RELATIONSHIPS, not a field-per-hex requirement.
 * Current mapping is sufficient to evaluate theme direction:
 *   canvas → page · surface-alt → surfaceMute · border → line ·
 *   grid-strong → shadowLine · headline gradient → accent · month states → semantics.
 * ⚠️ Watch `surfaceMute`: it currently serves both calendar headers AND alternate
 *   surfaces. If, after visual testing, "page looks right but the header reads too
 *   pale/dark", that's the trigger to add tokens — and only these two:
 *     surfaceSubtle  (very light tonal variation)
 *     grid           (calendar grid, split from `line`)
 * Do NOT add per-component skinning tokens (sidebar, calendarHeader, monthCell*, …)
 * unless a specific visual problem genuinely can't be solved with the above.
 */
export interface ElvaTheme {
  id: string;
  name: string;
  dark: boolean;
  page: string;
  surface: string;
  surfaceMute: string;
  line: string;
  shadowLine: string;
  ink: [string, string, string, string, string];
  accent: [string, string, string];
  success: string;
  gold: string;
  terracotta: string;
  info: string;
  bodyBackground?: string;
  bodyBackgroundSize?: string;
}

/*
 * FrontDesk Calendar color system — SATURATED / STRUCTURAL 3-theme set.
 * Design rule (this pass): a CLEAN NEUTRAL canvas + large SATURATED structural
 * anchors (blue glass sidebar + deep-blue calendar header, set as fixed anchors
 * in applyTheme) + vivid semantic colors. Maturity comes from composition and
 * restraint, NOT from desaturating the colors.
 *   page/surface = neutral (not a wash of pale blue/green) ·
 *   ink = neutral graphite ramp · accent triple = loud headline gradient ·
 *   semantics = deepened (viridian / amber / coral / clear-blue), shared per theme.
 * Default = Executive Cobalt.
 * (Dark themes are dropped for this focused test; the dark branch in applyTheme
 *  remains so a mature dark variant can be re-added later.)
 */
export const THEMES: ElvaTheme[] = [
  {
    // Repointed to the ELVA FrontDesk design system (rev 1). Indigo brand, cool
    // page ground, money-green / warn-amber / alert-red vocabulary. The runtime
    // theme engine maps these onto the legacy --cream/--ink/--ai/--success tokens;
    // the new role-named tokens (--surface-app, --money-green, …) live in elva.css.
    id: 'executive-cobalt',
    name: 'Executive Cobalt',
    dark: false,
    page: '#F3F5FB', // --surface-app
    surface: '#FFFFFF', // --surface-card
    surfaceMute: '#FAFBFE', // --surface-sunken
    line: '#E7EAF2', // --border-default
    shadowLine: '#D8DCE7', // --border-strong
    // text-primary · body(secondary) · secondary · muted · faint(disabled)
    ink: ['#242936', '#565E72', '#6C748A', '#98A0B2', '#B9C0CD'],
    // brand-indigo · indigo→violet mid · violet (the spectrum becomes on-brand)
    accent: ['#4C5BD4', '#6472DE', '#7B4BD4'],
    success: '#1F7A56', // --money-green
    gold: '#8A6415', // --warn-amber (text-safe amber; tints derive from it)
    terracotta: '#BE4440', // --alert-red
    info: '#4C5BD4', // links/selected fold into brand-indigo
  },
  {
    id: 'viridian-royal',
    name: 'Deep Viridian',
    dark: false,
    page: '#F5F7F6',
    surface: '#FFFFFF',
    surfaceMute: '#EEF3F0',
    line: '#DCE6E0',
    shadowLine: '#CDD9D1',
    ink: ['#18212B', '#2B3542', '#637085', '#8A96A8', '#A4AFBC'],
    accent: ['#0B7A64', '#258A86', '#2447C6'],
    success: '#0B7A64',
    gold: '#C98A12',
    terracotta: '#D95A4D',
    info: '#3E67D8',
  },
  {
    id: 'navy-brass',
    name: 'Midnight Navy & Brass',
    dark: false,
    page: '#F6F6F3',
    surface: '#FFFFFF',
    surfaceMute: '#EFEEE9',
    line: '#E0DED6',
    shadowLine: '#D0CDC2',
    ink: ['#18212B', '#2B3542', '#637085', '#8A96A8', '#A4AFBC'],
    accent: ['#203B68', '#3F6DA8', '#B98A2E'],
    success: '#0B7A64',
    gold: '#C98A12',
    terracotta: '#D95A4D',
    info: '#3E67D8',
  },
];

export const DEFAULT_THEME_ID = 'executive-cobalt';

export function getThemeById(id: string): ElvaTheme | undefined {
  return THEMES.find((t) => t.id === id);
}
